import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  Body1,
  Caption1,
  tokens,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Legend,
} from 'recharts';
import { ArrowSync20Regular, Delete20Regular } from '@fluentui/react-icons';
import { PageHeader } from '../components/PageHeader';
import { BrandIcon } from '../components/BrandIcon';
import { useActiveTenant } from '../hooks/useActiveTenant';
import { useAllScans } from '../hooks/useScans';
import { useTriggerScan } from '../hooks/useTriggerScan';
import { computeScoreBreakdown } from '../domain/scanEngine';
import { resetScans, seedDemoScansIfEmpty } from '../domain/scanStore';
import { mockTenants } from '../domain/mockTenants';
import { statusColors } from '../theme/brandTheme';
import { IS_LIVE_MODE } from '../api/liveMode';
import { azureScansApi, type AzureScan } from '../api/azureScansApi';
import { azureConnectionsApi } from '../api/azureConnectionsApi';

type ScanSource = 'm365' | 'azure';

// Plages temporelles du graphe d'évolution
type ChartRange = '1w' | '1m' | '3m' | '6m' | '1y';
const RANGE_DAYS: Record<ChartRange, number> = { '1w': 7, '1m': 30, '3m': 90, '6m': 180, '1y': 365 };
const RANGE_OPTS: [ChartRange, string][] = [
  ['1w', '1 sem.'], ['1m', '1 mois'], ['3m', '3 mois'], ['6m', '6 mois'], ['1y', '1 an'],
];

interface UnifiedRow {
  key:          string;
  source:       ScanSource;
  startedAt:    string;
  scope:        string;            // tenant (M365) ou nom de connexion (Azure)
  scopeSub?:    string;            // domaine (M365) ou subscriptionId (Azure)
  triggeredBy?: string;
  typeLabel:    string;
  total:        number;
  score:        number | null;
  counts:       { pass: number; warning: number; fail: number; manual: number; error: number };
  onOpen:       () => void;
}

export function ScansPage() {
  const { t, i18n }         = useTranslation();
  const { tenant, tenants } = useActiveTenant();
  const m365Scans   = useAllScans();
  const triggerScan = useTriggerScan();
  const isScanning  = triggerScan.isPending;
  const navigate    = useNavigate();

  // --- Scans Azure (live uniquement) : tous les scans du client + noms de connexion
  const azureQ = useQuery({
    queryKey:  ['azure-scans-all'],
    enabled:   IS_LIVE_MODE,
    staleTime: 15_000,
    retry:     false,
    queryFn:   async () => {
      const [scansRes, connRes] = await Promise.all([
        azureScansApi.listAll(50),
        azureConnectionsApi.list().catch(() => ({ connections: [] })),
      ]);
      // On retourne des donnees serialisables (tableaux) : stocker une Map dans le
      // cache react-query la casse (structural sharing -> objet simple sans .get).
      return { scans: scansRes.scans ?? [], connections: connRes.connections ?? [] };
    },
  });

  const onScanCurrent = () => triggerScan.mutate({ tenant, scanType: 'full' });
  void tenants;

  const onReset = () => {
    if (!confirm('Supprimer tous les scans LOCAUX (demo) et regenerer la demo ?')) return;
    resetScans();
    seedDemoScansIfEmpty();
  };

  // ---------------------- Lignes unifiees (M365 + Azure) ----------------------
  const rows: UnifiedRow[] = useMemo(() => {
    const out: UnifiedRow[] = [];

    // M365
    for (const scan of m365Scans) {
      const b = computeScoreBreakdown(scan);
      const tenantInfo = mockTenants.find((x) => x.id === scan.tenantId);
      out.push({
        key:         `m365-${scan.id}`,
        source:      'm365',
        startedAt:   scan.startedAt,
        scope:       tenantInfo?.name ?? scan.tenantId,
        scopeSub:    tenantInfo?.domain,
        triggeredBy: scan.meta.triggeredBy,
        typeLabel:   scan.meta.scanType === 'full' ? t('scans.type.full') : t('scans.type.partial'),
        total:       b.counts.total,
        score:       b.globalScore,
        counts:      { pass: b.counts.pass, warning: b.counts.warning, fail: b.counts.fail, manual: b.counts.manual, error: b.counts.error },
        onOpen:      () => navigate(`/scans/${scan.id}`),
      });
    }

    // Azure
    const azure = azureQ.data;
    const azNames = new Map((azure?.connections ?? []).map((c) => [c.id, c.name] as const));
    for (const s of (azure?.scans ?? [])) {
      out.push({
        key:         `azure-${s.id}`,
        source:      'azure',
        startedAt:   s.startedAt,
        scope:       azNames.get(s.connectionId) ?? t('scanBlocks.azure.title', 'Azure'),
        scopeSub:    s.subscriptionId,
        triggeredBy: undefined,
        typeLabel:   'Azure',
        total:       s.results?.length ?? 0,
        score:       azureScore(s),
        counts:      azureCounts(s),
        onOpen:      () => navigate('/azure'),
      });
    }

    // Tri global par date desc
    return out.sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  }, [m365Scans, azureQ.data, navigate, t]);

  // Serie temporelle pour le graphe d evolution (chronologique ascendant).
  // Chaque scan = un point ; M365 et Azure sont 2 series distinctes (connectNulls
  // relie les points d une meme source meme si l autre n a pas scanne au meme moment).
  const [chartRange, setChartRange] = useState<ChartRange>('3m');
  const chartData = useMemo(() => {
    const cutoff = Date.now() - RANGE_DAYS[chartRange] * 86_400_000;
    return [...rows]
      .filter((r) => new Date(r.startedAt).getTime() >= cutoff)
      .sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime())
      .map((r) => ({
        label: new Date(r.startedAt).toLocaleDateString(i18n.language, { day: '2-digit', month: '2-digit' }),
        m365:  r.source === 'm365'  ? r.score : null,
        azure: r.source === 'azure' ? r.score : null,
      }));
  }, [rows, i18n.language, chartRange]);

  return (
    <div>
      <PageHeader
        title={t('scans.title')}
        description={rows.length > 0
          ? t('scans.subtitle', { count: rows.length })
          : t('scans.subtitleEmpty')}
        actions={
          <>
            {!IS_LIVE_MODE && (
              <Button appearance="subtle" icon={<Delete20Regular />} onClick={onReset}>
                Reset demo
              </Button>
            )}
            <Button appearance="primary" icon={<ArrowSync20Regular />} onClick={onScanCurrent} disabled={isScanning}>
              {isScanning ? t('liveScanPanel.scanning') : t('scans.actions.launch', { tenant: tenant.name })}
            </Button>
          </>
        }
      />

      {/* Graphe d evolution du score (M365 + Azure) - des qu il y a >= 2 scans */}
      {chartData.length >= 2 && (
        <Card style={{ marginBottom: 16, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <Body1 style={{ fontWeight: 600 }}>{t('scans.evolution', 'Évolution du score')}</Body1>
            <div style={{ display: 'flex', gap: 4 }}>
              {RANGE_OPTS.map(([k, lbl]) => (
                <Button key={k} size="small" appearance={chartRange === k ? 'primary' : 'subtle'} onClick={() => setChartRange(k)}>{lbl}</Button>
              ))}
            </div>
          </div>
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={tokens.colorNeutralStroke3} />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} width={36} />
                <ReTooltip formatter={(v: number) => `${v} / 100`} />
                <Legend />
                <Line type="monotone" dataKey="m365"  stroke="#F25022" strokeWidth={2} dot={{ r: 3 }} connectNulls name="Microsoft 365" />
                <Line type="monotone" dataKey="azure" stroke="#0089D6" strokeWidth={2} dot={{ r: 3 }} connectNulls name="Microsoft Azure" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {rows.length === 0 ? (
        <MessageBar intent="info">
          <MessageBarBody>{t('scans.subtitleEmpty')}</MessageBarBody>
        </MessageBar>
      ) : (
        <Card>
          <Table arial-label={t('scans.title')}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: 120 }}>Source</TableHeaderCell>
                <TableHeaderCell>{t('scans.table.date')}</TableHeaderCell>
                <TableHeaderCell>{t('scans.table.tenant')}</TableHeaderCell>
                <TableHeaderCell>{t('scans.table.type')}</TableHeaderCell>
                <TableHeaderCell>{t('scans.table.controls')}</TableHeaderCell>
                <TableHeaderCell>{t('scans.table.score')}</TableHeaderCell>
                <TableHeaderCell>{t('scans.table.passWarnFail')}</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow
                  key={r.key}
                  onClick={r.onOpen}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = tokens.colorNeutralBackground2)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                >
                  <TableCell>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <BrandIcon name={r.source} size={18} />
                      <Body1 style={{ fontWeight: 600 }}>{r.source === 'm365' ? 'M365' : 'Azure'}</Body1>
                    </span>
                  </TableCell>
                  <TableCell>
                    <Body1>{new Date(r.startedAt).toLocaleString(i18n.language)}</Body1>
                    {r.triggeredBy && (
                      <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                        {r.triggeredBy}
                      </Caption1>
                    )}
                  </TableCell>
                  <TableCell>
                    <Body1 style={{ fontWeight: 600 }}>{r.scope}</Body1>
                    {r.scopeSub && (
                      <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block', fontFamily: 'Consolas, monospace', fontSize: 11 }}>
                        {r.scopeSub}
                      </Caption1>
                    )}
                  </TableCell>
                  <TableCell>{r.typeLabel}</TableCell>
                  <TableCell>{r.total}</TableCell>
                  <TableCell>
                    <Body1 style={{ fontSize: 18, fontWeight: 600, color: r.score == null ? tokens.colorNeutralForeground3 : scoreColor(r.score) }}>
                      {r.score == null ? '—' : r.score}
                    </Body1>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <PillCount color={statusColors.pass}    n={r.counts.pass}    title={t('dashboard.stats.compliant')} />
                      <PillCount color={statusColors.warning} n={r.counts.warning} title={t('dashboard.stats.improve')} />
                      <PillCount color={statusColors.fail}    n={r.counts.fail}    title={t('dashboard.stats.nonCompliant')} />
                      <PillCount color={statusColors.manual}  n={r.counts.manual}  title={t('dashboard.stats.manual')} />
                      <PillCount color={statusColors.error}   n={r.counts.error}   title={t('dashboard.stats.error')} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function azureCounts(s: AzureScan) {
  const r = s.results ?? [];
  return {
    pass:    r.filter((x) => x.status === 'pass').length,
    warning: r.filter((x) => x.status === 'warning').length,
    fail:    r.filter((x) => x.status === 'fail').length,
    manual:  r.filter((x) => x.status === 'manual' || x.status === 'not_applicable').length,
    error:   r.filter((x) => x.status === 'error').length,
  };
}

function azureScore(s: AzureScan): number | null {
  if (!s.results || s.results.length === 0) return null;
  return typeof s.globalScore === 'number' ? s.globalScore : null;
}

function PillCount({ color, n, title }: { color: string; n: number; title?: string }) {
  return (
    <span
      title={title}
      style={{
        display:        'inline-block',
        minWidth:       28,
        padding:        '2px 8px',
        borderRadius:   12,
        fontWeight:     600,
        fontSize:       12,
        textAlign:      'center',
        backgroundColor:`${color}20`,
        color,
      }}
    >
      {n}
    </span>
  );
}

function scoreColor(score: number): string {
  if (score >= 80) return statusColors.pass;
  if (score >= 60) return statusColors.warning;
  return statusColors.fail;
}
